import { useState } from "react";
import { Link, useLoaderData, useRevalidator } from "react-router-dom";
import { RichText } from "../components/RichText";
import { AccountUpdates } from "../components/AccountUpdates";
import { AdminOnly } from "../components/AdminOnly";
import { Avatar } from "../components/Avatar";
import { OwnerPicker } from "../components/OwnerPicker";
import { DomainsEditor } from "../components/DomainsEditor";
import { Field, Select, TextArea, TextInput } from "../components/Field";
import { HealthBadge, HEALTH_OPTIONS } from "../components/HealthDot";
import { Icon } from "../components/Icon";
import { NewRequestButton } from "../components/NewRequestButton";
import { Panel, PanelHead } from "../components/Panel";
import { RequestsTable } from "../components/RequestsTable";
import { Tabs, tabIds } from "../components/Tabs";
import { useSearchFilter } from "../data/useSearchFilter";
import { addContact, createRequest, deleteContact, updateAccount, updateContact } from "../data/api";
import { useRoster } from "../data/RosterContext";
import type { AccountDetailData } from "../data/loaders";
import type { Account, AccountHealth, Contact, ContactInput } from "../data/types";

export function AccountDetail() {
  const { account, requests } = useLoaderData() as AccountDetailData;
  const { members } = useRoster();
  const revalidator = useRevalidator();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  // The tab lives in the URL beside the table's filters, so a link or the
  // back button lands on the same view. Requests is the default.
  const [params, setFilter] = useSearchFilter();
  const wanted = params.get("tab");
  const tab: AccountTab = ACCOUNT_TABS.find((t) => t.key === wanted)?.key ?? "requests";
  const showTab = (next: AccountTab) => setFilter("tab", next === "requests" ? "" : next);

  const [name, setName] = useState(account.name);
  const [health, setHealth] = useState<AccountHealth>(account.health);
  const [leadOwnerId, setLeadOwnerId] = useState<string>(account.lead.id);
  const [plan, setPlan] = useState<Account["plan"]>(account.plan);
  const [brief, setBrief] = useState(account.brief);

  function startEdit() {
    setName(account.name);
    setHealth(account.health);
    setLeadOwnerId(account.lead.id);
    setPlan(account.plan);
    setBrief(account.brief);
    setEditing(true);
    // The notes and plan are edited on the Overview tab.
    showTab("overview");
  }

  async function save() {
    if (name.trim() === "") return;
    setSaving(true);
    try {
      await updateAccount(account.id, { name: name.trim(), health, leadOwnerId, plan, brief });
      revalidator.revalidate();
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mx-auto max-w-[1440px] px-6 py-8">
      <Link
        to="/accounts"
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-[var(--text-2)] transition-colors hover:text-[var(--text)] focus-visible:focus-ring"
      >
        <Icon name="arrow-left" />
        All accounts
      </Link>

      <header className="mb-6 flex flex-wrap items-start justify-between gap-3">
        {editing ? (
          <div className="flex flex-1 flex-wrap items-center gap-2">
            <TextInput aria-label="Account name" value={name} onChange={(e) => setName(e.target.value)} className="max-w-xs text-lg font-semibold" />
            <div className="w-32">
              <Select aria-label="Health" value={health} onChange={(e) => setHealth(e.target.value as AccountHealth)}>
                {HEALTH_OPTIONS.map((h) => (
                  <option key={h.value} value={h.value}>
                    {h.label}
                  </option>
                ))}
              </Select>
            </div>
            <div className="w-48">
              <OwnerPicker id="acc-lead" ariaLabel="CE lead" members={members} value={leadOwnerId} onChange={setLeadOwnerId} />
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3">
              <h1 className="text-display-xs font-semibold text-gray-900">{account.name}</h1>
              <HealthBadge health={account.health} />
            </div>
            <span className="flex items-center gap-2 text-sm text-[var(--text-2)]">
              <Avatar owner={account.lead} size="sm" />
              {account.lead.name} · CE lead
            </span>
          </>
        )}
        <div className="flex flex-none gap-2">
          {editing ? (
            <>
              <button
                type="button"
                onClick={() => setEditing(false)}
                className="rounded-md border border-[var(--border-strong)] bg-[var(--bg)] px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 focus-visible:focus-ring"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void save()}
                disabled={saving || name.trim() === ""}
                className="rounded-md bg-brand-600 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-700 focus-visible:focus-ring disabled:opacity-50"
              >
                Save
              </button>
            </>
          ) : (
            <>
              <a
                href={`/portal?preview=${encodeURIComponent(account.id)}`}
                target="_blank"
                rel="noopener"
                className="flex items-center gap-1.5 rounded-md border border-[var(--border-strong)] bg-[var(--bg)] px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 focus-visible:focus-ring"
              >
                View as customer
              </a>
              <NewRequestButton
                accounts={[account]}
                lockedAccount={account}
                onCreated={async (payload) => {
                  await createRequest(payload);
                  // Land on the table, where the new request now is.
                  showTab("requests");
                  revalidator.revalidate();
                }}
              />
              <button
                type="button"
                onClick={startEdit}
                className="flex items-center gap-1.5 rounded-md border border-[var(--border-strong)] bg-[var(--bg)] px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 focus-visible:focus-ring"
              >
                <Icon name="edit" />
                Edit
              </button>
            </>
          )}
        </div>
      </header>

      <Tabs label="Account" prefix="acc" value={tab} onChange={showTab} tabs={ACCOUNT_TABS} />

      <div role="tabpanel" id={tabIds("acc", "requests").panel} aria-labelledby={tabIds("acc", "requests").tab} hidden={tab !== "requests"}>
        {/* The Requests page's table, pinned to this account. */}
        <RequestsTable requests={requests} accounts={[account]} lockedAccount={account} />
      </div>

      <div
        role="tabpanel"
        id={tabIds("acc", "overview").panel}
        aria-labelledby={tabIds("acc", "overview").tab}
        hidden={tab !== "overview"}
        className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_280px]"
      >
        <div className="grid gap-5">
          <Panel labelledBy="brief-h">
            <PanelHead id="brief-h" title="Before you talk to them" />
            {/* Read view: pre-line keeps the blank lines people use to group
                notes, and break-words stops a pasted URL — this field is mostly
                links — from running past the panel and being clipped. */}
            {editing ? (
              <div className="p-4">
                <TextArea aria-label="Notes" value={brief} onChange={(e) => setBrief(e.target.value)} />
              </div>
            ) : (
              <RichText text={account.brief} />
            )}
          </Panel>
        </div>

        <aside className="grid h-max gap-4 rounded-xl border border-[var(--border)] bg-[var(--bg)] p-4">
          <ContactsEditor account={account} onChanged={() => revalidator.revalidate()} />
          <AdminOnly>
            <div className="border-t border-[var(--border)] pt-3">
              <DomainsEditor account={account} onChanged={() => revalidator.revalidate()} />
            </div>
          </AdminOnly>
          <div className="border-t border-[var(--border)] pt-3 text-sm">
            {editing ? (
              <Field label="Plan" htmlFor="acc-plan">
                <Select id="acc-plan" value={plan} onChange={(e) => setPlan(e.target.value as Account["plan"])}>
                  <option>Growth</option>
                  <option>Enterprise</option>
                </Select>
              </Field>
            ) : (
              <>
                <span className="text-[var(--text-2)]">Plan</span> <span className="font-medium">{account.plan}</span>
              </>
            )}
          </div>
        </aside>
      </div>
      {/* Mounted only while selected: it fetches, and the other tabs should not pay for that. */}
      {tab === "updates" && (
        <div role="tabpanel" id={tabIds("acc", "updates").panel} aria-labelledby={tabIds("acc", "updates").tab}>
          <AccountUpdates accountId={account.id} />
        </div>
      )}
    </div>
  );
}

type AccountTab = "requests" | "overview" | "updates";
const ACCOUNT_TABS: { key: AccountTab; label: string }[] = [
  { key: "requests", label: "Requests" },
  { key: "overview", label: "Overview" },
  { key: "updates", label: "Updates" },
];

const EMPTY: ContactInput = { name: "", role: "", email: "" };

function ContactsEditor({ account, onChanged }: { account: Account; onChanged: () => void }) {
  const [editingId, setEditingId] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<ContactInput>(EMPTY);
  const [busy, setBusy] = useState(false);

  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    try {
      await fn();
      onChanged();
      setEditingId(null);
      setAdding(false);
      setDraft(EMPTY);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wide text-[var(--text-3)]">Contacts</span>
        {!adding && (
          <button
            type="button"
            onClick={() => {
              setDraft(EMPTY);
              setAdding(true);
              setEditingId(null);
            }}
            className="text-xs font-medium text-brand-600 hover:text-brand-700 focus-visible:focus-ring"
          >
            Add
          </button>
        )}
      </div>

      <div className="grid gap-3">
        {account.contacts.map((c) =>
          editingId === c.id ? (
            <ContactForm key={c.id} value={draft} busy={busy} onChange={setDraft} onCancel={() => setEditingId(null)} onSave={() => void run(() => updateContact(c.id, draft))} />
          ) : (
            <ContactCard key={c.id} contact={c} onEdit={() => { setDraft({ name: c.name, role: c.role, email: c.email }); setEditingId(c.id); setAdding(false); }} onDelete={() => void run(() => deleteContact(c.id))} />
          ),
        )}

        {adding && (
          <ContactForm value={draft} busy={busy} onChange={setDraft} onCancel={() => setAdding(false)} onSave={() => void run(() => addContact(account.id, draft))} />
        )}

        {account.contacts.length === 0 && !adding && <p className="text-sm text-[var(--text-2)]">No contacts yet.</p>}
      </div>
    </div>
  );
}

function ContactCard({ contact, onEdit, onDelete }: { contact: Contact; onEdit: () => void; onDelete: () => void }) {
  return (
    <div className="group text-sm">
      <div className="flex items-center justify-between">
        <div className="font-medium text-gray-800">{contact.name}</div>
        <div className="flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
          <button type="button" onClick={onEdit} aria-label={`Edit ${contact.name}`} className="rounded p-0.5 text-[var(--text-2)] hover:text-[var(--text)] focus-visible:focus-ring">
            <Icon name="edit" />
          </button>
          <button type="button" onClick={onDelete} aria-label={`Remove ${contact.name}`} className="rounded p-0.5 text-[var(--text-2)] hover:text-error-600 focus-visible:focus-ring">
            <Icon name="x" />
          </button>
        </div>
      </div>
      <div className="text-[var(--text-2)]">{contact.role}</div>
      <a href={`mailto:${contact.email}`} className="text-brand-600 hover:text-brand-700 focus-visible:focus-ring">
        {contact.email}
      </a>
    </div>
  );
}

function ContactForm({ value, busy, onChange, onSave, onCancel }: { value: ContactInput; busy: boolean; onChange: (v: ContactInput) => void; onSave: () => void; onCancel: () => void }) {
  const invalid = value.name.trim() === "" || value.email.trim() === "";
  return (
    <div className="grid gap-2 rounded-md border border-[var(--border)] p-2">
      <TextInput aria-label="Contact name" value={value.name} onChange={(e) => onChange({ ...value, name: e.target.value })} placeholder="Name" />
      <TextInput aria-label="Contact role" value={value.role} onChange={(e) => onChange({ ...value, role: e.target.value })} placeholder="Role" />
      <TextInput aria-label="Contact email" value={value.email} onChange={(e) => onChange({ ...value, email: e.target.value })} placeholder="Email" />
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="rounded-md px-2 py-1 text-xs font-medium text-[var(--text-2)] hover:text-[var(--text)] focus-visible:focus-ring">
          Cancel
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={busy || invalid}
          className="rounded-md bg-brand-600 px-2 py-1 text-xs font-semibold text-white hover:bg-brand-700 focus-visible:focus-ring disabled:opacity-50"
        >
          Save
        </button>
      </div>

    </div>
  );
}
